import useAccessGroupMembersApi, {
    useAccessGroupMembersApiDepartments,
    useAccessGroupMembersApiEmployees,
} from "features/admin/access-groups/single/useAccessGroupMembers";

import AccessGroupMemberFormShowDropdown from "features/admin/access-groups/single/AccessGroupMemberFormShowDropdown";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import Options from "shared/components/inputs/form-fields/Options";
import PropTypes from "prop-types";
import React from "react";
import VStack from "shared/components/layout/stack/VStack";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { useWatch } from "react-hook-form";

const memberTypeNameOptions = [
    { label: "Add Employee", value: "Employee" },
    { label: "Add Department", value: "Department" },
];

function transformMemberDataToFormData(memberData = {}) {
    switch (memberData.type) {
        case "U":
            return {
                employee: memberData.memberId,
                memberTypeName: memberTypeNameOptions[0].value,
            };

        case "D":
            return {
                department: memberData.memberId,
                memberTypeName: memberTypeNameOptions[1].value,
            };

        default:
            return {};
    }
}

function transformFormDataToMemberData(formData = {}, memberData = {}) {
    const newMemberData = { ...memberData };

    switch (formData.memberTypeName) {
        case memberTypeNameOptions[0].value:
            newMemberData.memberId = formData.employee;
            newMemberData.type = "U";
            return newMemberData;

        case memberTypeNameOptions[1].value:
            newMemberData.memberId = formData.department;
            newMemberData.type = "D";
            return newMemberData;

        default:
            return newMemberData;
    }
}

function getDepartmentsDropdownOptions(departments = []) {
    return departments.map((department) => ({
        value: department.Id,
        label: department.Name,
    }));
}

function getEmployeesDropdownOptions(employees = []) {
    return employees.map((employee) => ({
        label: employee.FullName,
        value: employee.NewId,
    }));
}

const ModalCTA = ({ isLoading, onClose }) => {
    const { memberTypeName, employee, department } = useWatch();

    const isDisabledFallback = memberTypeName === memberTypeNameOptions[1].value ? !department : true;
    const isDisabled = memberTypeName === memberTypeNameOptions[0].value ? !employee : isDisabledFallback;

    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                disabled: isDisabled,
                label: (() => {
                    if (memberTypeName === memberTypeNameOptions[0].value) return "Add Employee";
                    if (memberTypeName === memberTypeNameOptions[1].value) return "Add Department";
                    return "Add";
                })(),
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

const AccessGroupMemberForm = ({ accessGroupId, accessGroupName, onClose }) => {
    const { useAccessGroupMembers } = useAccessGroupMembersApi();
    const memberData = useAccessGroupMembers(accessGroupId);

    const { createAccessGroupMember } = useAccessGroupMembersApi();
    const { employees } = useAccessGroupMembersApiEmployees();
    const { departments } = useAccessGroupMembersApiDepartments();

    if (departments.isError || employees.isError || memberData.isError) {
        return <div>Error</div>;
    }

    const modifier = createAccessGroupMember;

    const currentMemberData = {};

    const existingEmployees = memberData.isLoading
        ? []
        : memberData.data
              .filter((emp) => emp.type === "U")
              .map((d) => ({
                  value: d.memberId,
              }));

    const existingDepartments = memberData.isLoading
        ? []
        : memberData.data
              .filter((dept) => dept.type === "D")
              .map((data) => ({
                  value: data.memberId,
              }));

    const departmentsDropdownOptions = getDepartmentsDropdownOptions(
        departments.data
    );

    const employeesDropdownOptions = getEmployeesDropdownOptions(
        employees.data
    );

    const getMemberName = (id) => {
        return employeesDropdownOptions.find((emp) => emp.value === id);
    };

    const getDepartmentName = (id) => {
        return departmentsDropdownOptions.find((dept) => dept.value === id);
    };

    const onSubmit = async (formData) => {
        let memberName = null;
        if (formData.memberTypeName === memberTypeNameOptions[0].value) {
            memberName = getMemberName(formData.employee).label;
        } else if (formData.memberTypeName === memberTypeNameOptions[1].value) {
            memberName = getDepartmentName(formData.department).label;
        }

        const membersData = transformFormDataToMemberData(
            formData,
            currentMemberData
        );

        const allData = { groupId: accessGroupId, body: membersData };

        await modifier.mutateAsync(allData);
        onClose();
        sendNotification(
            <>
                <b>{memberName}</b> has been added to{" "}
                <b>{accessGroupName}</b>.
            </>
        );
    };

    return (
        <Form
            style={{ overflowY: "auto", height: "500px" }}
            onSubmit={onSubmit}
            defaultValues={transformMemberDataToFormData(currentMemberData)}
        >
            <VStack spacing="Three">
                <VStack style={{ minHeight: 350 }}>
                    <Options
                        name="memberTypeName"
                        label="Are you adding an Employee or a Department?"
                        options={memberTypeNameOptions}
                        multiselect={false}
                    />
                    <AccessGroupMemberFormShowDropdown
                        employeesDataLoading={
                            employees.isLoading || memberData.isLoading
                        }
                        departmentsDataLoading={
                            departments.isLoading || memberData.isLoading
                        }
                        employeesDropdownOptions={employeesDropdownOptions}
                        departmentsDropdownOptions={departmentsDropdownOptions}
                        existingEmployeesList={existingEmployees}
                        existingDepartmentsList={existingDepartments}
                    />
                </VStack>
                <ModalCTA isLoading={modifier.isLoading} onClose={onClose} />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

AccessGroupMemberForm.propTypes = {
    accessGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    accessGroupName: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired
};

export default AccessGroupMemberForm;
